import bitmage.ByteOrder
import bitmage.decodeBase85
import bitmage.fromHex
import bitmage.stripLeadingZeros
import bitmage.toBytes
import decoders.*
import decoders.apple.*
import decoders.web.*
import preprocessing.And
import preprocessing.BytewiseCalc
import preprocessing.Preprocessor
import preprocessing.Xor
import preprocessing.Reverse
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.time.measureTime

object ByteWitch {
    private val preprocessors = listOf<Preprocessor>(Reverse, Xor, And, BytewiseCalc)
    private val preprocCommands = preprocessors.associateBy { it.command }

    private val decoders = listOf<ByteWitchDecoder>(
        BPList17, BPList15, BPListParser, AppleArchive, AppleCompression, Utf8Decoder, Utf16Decoder, JWT,
        OpackParser, MsgPackParser, CborParser, BsonParser, UbjsonParser, JsonDecoder,
        ProtobufParser, ASN1BER, Sec1Ec, TLS12, LengthPrefixDecoder, TLV8, TLV16, TLV816, IEEE754, MSZIP, Bech32,
        DMAP, NotarizedTicket, AppleAuth, HTTP2, Argo, WebSocket,
        GenericTLV, EdDSA, ECCurves, Randomness, HeuristicSignatureDetector,
        /* PGP, ModernPGP */
    )

    // Decoders that may be used to segment unknown payloads as SSF preprocessing
    val segmentFindingDecoders = listOf<ByteWitchDecoder>(Utf16Decoder, BPListParser, ASN1BER)

    enum class Encoding(val label: String) {
        NONE("none"), PLAIN("plain"), HEX("hex"), DECIMAL("decimal"), HEXDUMP("hexdump"), BASE64("base64"), BASE85("ascii85")
    }

    fun stripComments(data: String, keepWhitespace: Boolean = false): String {
        // allow use of # as line comment
        val linesep = if(keepWhitespace) "\n" else ""
        val stripped = data.split("\n").joinToString(linesep) { line ->
            val commentMarker = line.indexOfFirst { it == '#' }
            val lineEnd = if (commentMarker == -1) line.length else commentMarker
            if(keepWhitespace)
                line.take(lineEnd)
            else
                line.take(lineEnd).replace(Regex("\\s+"), "")
        }

        return stripped
    }

    fun getBytesFromInputEncoding(data: String): Pair<ByteArray, Encoding> {
        var cleanedData = data.trim()
        val preprocessingCommands = mutableListOf<Pair<Preprocessor, String>>()

        if(cleanedData.isEmpty())
            return Pair(byteArrayOf(), Encoding.NONE)

        // save recognized preprocessor commands for later
        while(preprocCommands.keys.any { cmd -> cleanedData.startsWith("#$cmd") }) {
            var lineEnd = cleanedData.indexOf('\n')
            if(lineEnd == -1)
                lineEnd = cleanedData.length

            var cmdEnd = lineEnd
            val spaceIdx = cleanedData.indexOf(' ')
            if(spaceIdx in 1..<lineEnd)
                cmdEnd = spaceIdx

            val args = cleanedData.substring(cmdEnd, lineEnd)
            val cmd = cleanedData.substring(1, cmdEnd)
            cleanedData = cleanedData.substring(lineEnd+1)

            val preprocessor = preprocCommands[cmd]!!
            preprocessingCommands.add(Pair(preprocessor, args))
        }

        cleanedData = cleanedData.trim()

        // allow some overrides
        var isPlain = false
        var isDecimal = false
        if(cleanedData.startsWith("#plain")) {
            isPlain = true
            cleanedData = cleanedData.removePrefix("#plain").trim()
        }
        else if(cleanedData.startsWith("#decimal") || cleanedData.startsWith("#numeric")) {
            isDecimal = true
            cleanedData = cleanedData.removePrefix("#decimal").removePrefix("#numeric").trim()
        }

        // note: in a bit of a hack, we support both classical base64 and base64url encodings here (-_ being url-only chars)
        val isBase64 = cleanedData.removePrefix("0x").replace("\n", "").matches(Regex("^[A-Z0-9+/\\-_~]+[G-Z+/\\-_~][A-Z0-9+/\\-_~]{4,}={0,2}$", RegexOption.IGNORE_CASE)) // matches b64 charset and at least one char distinguishing from raw hex
        val isHexdump = cleanedData.contains(Regex("^[0-9a-f]+\\s+([0-9a-f]{2}\\s+)+\\s*\\|.*\\|\\s*$", setOf(RegexOption.IGNORE_CASE, RegexOption.MULTILINE)))
        val commentsStripped = stripComments(cleanedData).removePrefix("0x")
        val isHex = Regex("[0-9a-fA-F\\s]+").matches(commentsStripped)
        val isBase85 = (cleanedData.startsWith("<~") || cleanedData.length > 250) && cleanedData.removePrefix("<~").removeSuffix("~>").replace("\n", "").matches(Regex(
            "^[0-9A-Za-uyz\\[\\]!\"#$%&'()*+,\\-./:;<=>?@\\\\^_`]+$"
        ))

        val decode = when {
            isPlain -> Pair(cleanedData.encodeToByteArray(), Encoding.PLAIN)
            isDecimal -> {
                if(cleanedData.isBlank())
                    Logger.showUserVisibleMessage("numeric input: acceptable formats are 1337 0b1001 0xCAFE (and other base notations: 0t2, 0q3, 0Q4, 0s5, 0S6, 0o7, 0n8). whitespace separated numbers are concatenated, format mixing is supported.")

                val parsed = parseDecimals(stripComments(cleanedData, keepWhitespace = true))
                if(parsed != null)
                    Pair(parsed, Encoding.DECIMAL)
                else {
                    Logger.showUserVisibleMessage("malformed decimal input")
                    Pair(byteArrayOf(), Encoding.NONE)
                }
            }
            isBase64 -> Pair(decodeBase64(cleanedData.replace("\n", "")), Encoding.BASE64)
            isHexdump -> Pair(decodeHexdump(cleanedData), Encoding.HEXDUMP)
            isHex -> {
                val filtered = commentsStripped.filter { it in "0123456789abcdefABCDEF" }
                if (filtered.length % 2 != 0)
                    Pair(byteArrayOf(), Encoding.NONE)
                else
                    Pair(filtered.fromHex(), Encoding.HEX)
            }
            isBase85 -> Pair(decodeBase85(cleanedData), Encoding.BASE85)
            else -> Pair(cleanedData.encodeToByteArray(), Encoding.PLAIN)
        }

        var processedBytes = decode.first
        preprocessingCommands.forEach { (proc, args) ->
            Logger.log("Applying preprocessor '${proc.command}' with args '$args'")
            val preprocOut = proc.process(args, processedBytes)
            processedBytes = preprocOut.first
            if(preprocOut.second != null) {
                Logger.showUserVisibleMessage(preprocOut.second!!)
                Logger.tag("preprocessing", preprocOut.second!!)
            }
        }

        return Pair(processedBytes, decode.second)
    }


    fun analyze(data: ByteArray, tryhard: Boolean): List<Pair<String, ByteWitchResult>> {
        Logger.clearUserVisibleMessage()
        val allDecoders = decoders

        if(tryhard) {
            Logger.log("tryhard decode attempt...")
            return allDecoders.mapNotNull {
                val decode: ByteWitchResult?
                val elapsed = measureTime {
                    decode = it.tryhardDecode(data)
                }
                Logger.log("decode with ${it.name} (${elapsed}) yielded $decode")
                if (decode != null) Pair(it.name, decode) else null
            }
        }
        else {
            // decodes as valid gives a quick estimate of which decoders could decode a payload
            // this is not necessarily true, so we catch failed parses later on also and remove them from the results
            val possibleDecoders: List<Pair<ByteWitchDecoder, Pair<Double, ByteWitchResult?>>>
            var elapsed = measureTime {
                possibleDecoders = decoders.map {
                    val result: Pair<ByteWitchDecoder, Pair<Double, ByteWitchResult?>>
                    val elapsed = measureTime {
                        result = Pair(it, it.confidence(data, 0))
                    }
                    Logger.tag("Performance", "confidence check of ${it.name} took $elapsed")
                    result
                }.filter { it.second.first > 0.45 }
            }
            Logger.tag("Performance", "got ${possibleDecoders.size} plausible decoders in $elapsed")

            val successfulDecodes: List<Pair<String, ByteWitchResult>>
            elapsed = measureTime {
                successfulDecodes = possibleDecoders.mapNotNull {
                    try {
                        Pair(it.first.name, it.second.second ?: it.first.decode(data, 0))
                    } catch (e: Exception) {
                        //Logger.log(e.toString())
                        null
                    }
                }
            }
            Logger.tag("Performance", "full decoding (${successfulDecodes.size} results) took $elapsed")

            return successfulDecodes
        }
    }

    fun quickDecode(data: ByteArray, sourceOffset: Int): ByteWitchResult? {

        decoders.forEach { decoder ->
            val confidence = decoder.confidence(data, sourceOffset)
            if (confidence.first > 0.75) {
                try {
                    return confidence.second ?: decoder.decode(data, sourceOffset, inlineDisplay = true)
                } catch (e: Exception) {
                    Logger.log("Quick decode failed with exception: '${e.message}' when assuming ${decoder.name} format with confidence ${confidence.first}")
                    e.printStackTrace()
                }
            }
        }

        return null
    }

    private fun decodeHexdump(hexdumpData: String): ByteArray {
        var collectedBytes = byteArrayOf()

        // Process the hexdump line by line
        hexdumpData.lines().forEach { line ->
            // Skip blank lines
            if (line.isBlank()) return@forEach

            // Remove the first column: everything until the first space
            val firstSpaceIndex = line.indexOf(' ')
            if (firstSpaceIndex == -1) return@forEach

            // Get the rest of the line after the address, and trim leading spaces
            var hexPart = line.substring(firstSpaceIndex).trimStart()

            // If the line contains a '|' character, only take the part before it
            val pipeIndex = hexPart.indexOf('|')
            if (pipeIndex != -1) {
                hexPart = hexPart.substring(0, pipeIndex).trim()
            }

            collectedBytes += hexPart.filter { it in "0123456789abcdefABCDEF" }.fromHex()
        }

        return collectedBytes
    }

    @OptIn(ExperimentalEncodingApi::class)
    fun decodeBase64(base64Data: String): ByteArray {
        // transform URL-safe base64 into regular base64
        // somewhat interestingly, Apple sometimes uses "~" in place of "=" as padding in URL-safe B64 - despite the standard specifying no padding
        val canonicalB64 = base64Data.replace('-', '+').replace('_', '/').replace('~', '=')

        // b64 string is already padded!
        val padded = if(canonicalB64.endsWith("="))
            canonicalB64
        else {
            when (canonicalB64.length % 4) {
                0 -> canonicalB64
                2 -> "$canonicalB64=="
                3 -> "$canonicalB64="
                else -> "" // illegal unpadded b64
            }
        }

        return try {
            Base64.Default.decode(padded)
        }
        catch (e: Exception) {
            byteArrayOf()
        }
    }

    fun parseDecimals(input: String): ByteArray? {
        val preprocessed = input.replace(',', ' ')
        val numbers = preprocessed.split(" ", "\n").filter { !it.matches(Regex("\\s*")) }.map {
            when {
                it.startsWith("0x") -> {
                    val hex = it.removePrefix("0x")
                    // pad to full bytes
                    val padded = if(hex.length % 2 == 0) hex else "0$hex"
                    padded.fromHex()
                }
                it.startsWith("0b") -> {
                    val binary = it.removePrefix("0b")
                    val padding = binary.length % 8
                    val padded = "0".repeat(padding) + binary
                    padded.chunked(8).map { byte -> byte.toInt(2).toByte() }.toByteArray()
                }
                it.startsWith("0") && it.length > 2 && it[1] in "tqQsSon" -> {
                    val baseID = it[1]
                    val base = when(baseID){
                        // bit of an arcane shorthand but okay
                        't' -> 3
                        'q' -> 4
                        'Q' -> 5
                        's' -> 6
                        'S' -> 7
                        'o' -> 8
                        'n' -> 9
                        else -> return null
                    }
                    val digits = it.substring(2)
                    val value = try { digits.toLong(base) } catch (e: Exception) { return null }
                    value.toBytes(ByteOrder.BIG).stripLeadingZeros()
                }
                it.matches(Regex("\\d+")) -> {
                    it.toLong().toBytes(ByteOrder.BIG).stripLeadingZeros()
                }
                else -> byteArrayOf()
            }
        }

        return numbers.fold(byteArrayOf()) { buf, elem -> buf + elem }
    }
}